import { databaseHostSchema, okSchema, type DatabaseHost } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Pencil, Plus, Trash2, Database as DatabaseIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Confirm } from '@/components/confirm';
import { FormField } from '@/components/form-field';
import { PageHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { apiRequest } from '@/lib/api';
import { nodesQuery } from '@/lib/admin-queries';
import { errorMessage } from '@/lib/forms';

export const Route = createFileRoute('/_app/admin/database-hosts')({
  component: DatabaseHostsPage,
});

const key = ['admin', 'database-hosts'];

function DatabaseHostsPage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      apiRequest('/api/application/database-hosts', {
        schema: z.object({ data: z.array(databaseHostSchema) }),
        signal,
      }).then((r) => r.data),
  });
  const [editing, setEditing] = useState<DatabaseHost | 'new' | null>(null);
  const remove = useMutation({
    mutationFn: (h: DatabaseHost) =>
      apiRequest(`/api/application/database-hosts/${h.id}`, { method: 'DELETE', schema: z.null() }),
    onSuccess: () => {
      toast.success('Host removed.');
      void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <>
      <PageHeader
        title="Database hosts"
        icon={DatabaseIcon}
        description="MySQL or MariaDB servers that servers’ databases are created on."
        actions={
          <Button onClick={() => setEditing('new')}>
            <Plus /> Add host
          </Button>
        }
      />
      <QueryState query={query}>
        {(hosts) =>
          hosts.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No hosts yet. Add one so servers can have databases.
            </p>
          ) : (
            <div className="table-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Databases</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hosts.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell className="font-medium">{h.name}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {h.host}:{h.port}
                        {h.publicHost && (
                          <div className="text-muted-foreground">shown as {h.publicHost}</div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{h.username}</TableCell>
                      <TableCell className="tabular-nums">
                        {h.databaseCount}
                        {h.maxDatabases ? ` / ${h.maxDatabases}` : ''}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => setEditing(h)}>
                            <Pencil /> Edit
                          </Button>
                          <Confirm
                            title={`Remove ${h.name}?`}
                            description="Only possible when no databases are on it."
                            action="Remove"
                            destructive
                            onConfirm={() => remove.mutate(h)}
                          >
                            <Button
                              variant="outline-destructive"
                              size="icon"
                              aria-label={`Remove ${h.name}`}
                            >
                              <Trash2 />
                            </Button>
                          </Confirm>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )
        }
      </QueryState>
      {editing && (
        <HostDialog
          host={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => void queryClient.invalidateQueries({ queryKey: key })}
        />
      )}
    </>
  );
}

function HostDialog({
  host,
  onClose,
  onSaved,
}: {
  host: DatabaseHost | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const nodes = useQuery(nodesQuery);
  const [f, setF] = useState({
    name: host?.name ?? '',
    host: host?.host ?? '',
    port: String(host?.port ?? 3306),
    publicHost: host?.publicHost ?? '',
    username: host?.username ?? '',
    password: '',
    maxDatabases: String(host?.maxDatabases ?? 0),
    nodeId: host?.nodeId ? String(host.nodeId) : 'any',
  });
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));
  const body = () => ({
    name: f.name,
    host: f.host,
    port: Number(f.port),
    publicHost: f.publicHost || null,
    username: f.username,
    ...(f.password ? { password: f.password } : {}),
    maxDatabases: Number(f.maxDatabases) || 0,
    nodeId: f.nodeId === 'any' ? null : Number(f.nodeId),
  });
  const test = useMutation({
    mutationFn: () =>
      apiRequest('/api/application/database-hosts/test', {
        method: 'POST',
        body: { host: f.host, port: Number(f.port), username: f.username, password: f.password },
        schema: okSchema.extend({ version: z.string() }),
      }),
    onSuccess: (r) => toast.success(`Connected. ${r.version}`),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/application/database-hosts${host ? `/${host.id}` : ''}`, {
        method: host ? 'PUT' : 'POST',
        body: body(),
        schema: databaseHostSchema,
      }),
    onSuccess: () => {
      toast.success('Host saved.');
      onSaved();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{host ? `Edit ${host.name}` : 'Add a database host'}</DialogTitle>
          <DialogDescription>
            The account needs permission to create databases and users (root works). The password is
            stored encrypted and never shown again. The connection is tested before saving.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <FormField label="Name">
            <Input value={f.name} onChange={(e) => set('name', e.target.value)} required />
          </FormField>
          <FormField label="Host">
            <Input
              value={f.host}
              onChange={(e) => set('host', e.target.value)}
              required
              placeholder="127.0.0.1"
            />
          </FormField>
          <FormField label="Port">
            <Input
              type="number"
              value={f.port}
              onChange={(e) => set('port', e.target.value)}
              required
            />
          </FormField>
          <FormField
            label="Address shown to users (optional)"
            description="If game servers reach it by another name."
          >
            <Input value={f.publicHost} onChange={(e) => set('publicHost', e.target.value)} />
          </FormField>
          <FormField label="User">
            <Input
              value={f.username}
              onChange={(e) => set('username', e.target.value)}
              required
              autoComplete="off"
            />
          </FormField>
          <FormField
            label="Password"
            description={host ? 'Leave empty to keep the stored password.' : undefined}
          >
            <Input
              type="password"
              value={f.password}
              onChange={(e) => set('password', e.target.value)}
              required={!host}
              autoComplete="new-password"
            />
          </FormField>
          <FormField label="Most databases (0 = no limit)">
            <Input
              type="number"
              min={0}
              value={f.maxDatabases}
              onChange={(e) => set('maxDatabases', e.target.value)}
            />
          </FormField>
          <FormField label="Offer to servers on">
            <Select value={f.nodeId} onValueChange={(v) => set('nodeId', v)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Every node</SelectItem>
                {(nodes.data ?? []).map((n) => (
                  <SelectItem key={n.id} value={String(n.id)}>
                    {n.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              disabled={test.isPending || !f.password}
              onClick={() => test.mutate()}
            >
              {test.isPending ? 'Testing…' : 'Test connection'}
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save host'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
